<template>
  <v-container>
    <v-responsive
      class="align-center mx-auto"
      max-width="1000"
    >
      <h1 class="text-h4 font-weight-black pt-8 pb-4 pl-3">
        Training Materials
      </h1>

      <v-alert
        v-if="error"
        color="error"
        icon="mdi-alert-circle"
        title="Error Loading Training Materials"
        variant="tonal"
        border="start"
        class="mb-4"
        closable
        @click:close="error = null"
      >
        {{ error }}
      </v-alert>

      <v-card
        class="rounded-xl"
        elevation="0"
      >
        <v-card-title class="d-block pa-6">
          <SearchField
            v-model="search"
            placeholder="Search training materials..."
            class="mb-6"
          />
          
          <TagFilter
            v-model="selectedTags"
            :available-tags="availableTags"
          />
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="filteredTrainingMaterials"
          :search="search"
          :loading="loading"
          hover
          class="document-table"
        >
          <template #[`item.title`]="{ item }">
            <v-btn
              variant="text"
              color="primary"
              class="pa-0 text-none text-body-1"
              @click="handleTrainingMaterialDownload(item)"
            >
              {{ item.title }}
            </v-btn>
          </template>

          <template #[`item.size`]="{ item }">
            <span class="text-medium-emphasis">
              {{ formatFileSize(item.file.size) }}
            </span>
          </template>

          <template #[`item.tags`]="{ item }">
            <v-chip
              v-for="tag in item.tags"
              :key="tag.id"
              class="me-2"
              size="small"
              variant="tonal"
              color="primary"
            >
              {{ tag.name }}
            </v-chip>
          </template>

          <template #[`item.created`]="{ item }">
            <span class="text-medium-emphasis">
              {{ new Date(item.created!).toLocaleDateString() }}
            </span>
          </template>

          <template #[`item.modified`]="{ item }">
            <span class="text-medium-emphasis">
              {{ new Date(item.modified!).toLocaleDateString() }}
            </span>
          </template>
        </v-data-table>
      </v-card>
    </v-responsive>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useTrainingMaterialStore } from '@/stores/training_material'
import type { ITag } from '@/models/tag/tag.types'
import { SystemError } from '@/models/error'
import TagFilter from '@/components/common/TagFilter.vue'
import SearchField from '@/components/common/SearchField.vue'
import { formatFileSize, downloadFile } from '@/utils/file'
import type { ITrainingMaterial } from '@/models/training_material'
import { analyticsService } from '@/services/analytics'

const trainingMaterialStore = useTrainingMaterialStore()
const { getTrainingMaterials } = storeToRefs(trainingMaterialStore)

const search = ref('')
const loading = ref(true)
const error = ref<string | null>(null)
const selectedTags = ref<number[]>([])

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
})

// Get unique tags from all training materials
const availableTags = computed(() => {
  const tagMap = new Map<number, ITag>()
  getTrainingMaterials.value?.forEach(doc => {
    doc.tags?.forEach(tag => {
      if (tag.id) {
        tagMap.set(tag.id, tag)
      }
    })
  })
  return Array.from(tagMap.values())
})

// Filter training materials based on selected tags
const filteredTrainingMaterials = computed(() => {
  if (!selectedTags.value.length) return getTrainingMaterials.value

  return getTrainingMaterials.value?.filter(doc => 
    doc.tags?.some(tag => tag.id && selectedTags.value.includes(tag.id))
  )
})

const headers = [
  { 
    title: 'Title',
    key: 'title',
    align: 'start' as const,
    width: '40%'
  },
  { 
    title: 'Size',
    key: 'size',
    align: 'start' as const,
    width: '100px'
  },
  { 
    title: 'Tags',
    key: 'tags',
    align: 'start' as const,
    width: '25%'
  },
  { 
    title: 'Created',
    key: 'created',
    align: 'start' as const,
    width: '120px'
  },
  { 
    title: 'Modified',
    key: 'modified',
    align: 'start' as const,
    width: '120px'
  }
]

async function fetchTrainingMaterials() {
  try {
    loading.value = true
    error.value = null
    await trainingMaterialStore.fetchTrainingMaterials()
  } catch (e) {
    if (e instanceof SystemError) {
      error.value = e.detail || e.message
    } else {
      error.value = 'Failed to load training materials'
    }
  } finally {
    loading.value = false
  }
}

async function handleTrainingMaterialDownload(material: ITrainingMaterial) {
  try {
    await downloadFile(material.file, material.title)

    // TODO: Add training material type - tags
    analyticsService.track('Training Material Downloaded', {
      'Training Material Name': material.title
    })

    // Show success snackbar
    snackbar.value = {
      show: true,
      text: `${material.title} downloaded successfully`,
      color: 'success'
    }
  } catch (e) {
    console.error('Download failed:', e)
    error.value = 'Failed to download training material'
  }
}


onMounted(() => {
  fetchTrainingMaterials()
})
</script>

<style scoped>
.v-card-title {
  padding: 24px 24px 16px;
}

:deep(.document-table) {
  .v-data-table-header__content {
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  .v-data-table__tr {
    transition: all 0.2s ease-in-out;
    
    &:hover {
      .v-btn {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
      }
    }
  }

  .v-data-table__td {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .v-chip {
    font-weight: 500;
  }
}

:deep(.v-card) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  
  .v-table {
    background: transparent;
  }
}

:deep(.v-alert) {
  border-radius: 8px;
}
</style>
